import { USER_ROLES } from '@constants/user'
import type { RegionServerKeys } from '@/constants/region'
import type {
  User,
  BillingAddress,
  FeatureLimit,
  SpaceType,
  AllowedApp,
} from './index'

export const OrgRole = {
  Admin: USER_ROLES.admin,
  Member: USER_ROLES.member,
  Owner: USER_ROLES.owner,
} as const

export const OrgSidebarLogoTypes = {
  Default: 'default',
  Custom: 'custom',
} as const

export type OrgSpace = {
  name: string
  id: number
  euid: null | string
  region: string
  owner_id: number
  updated_at: Date
  plan: string
  plan_level: number
  trial: boolean
  requires_2fa: boolean | null
  created_at: Date
  space_type: SpaceType
}

export type SpaceRoles = {
  [key: string]: string[]
}
export type UsersOnSpaces = {
  [key: string]: {
    user_id: number
    space_ids: number[]
    space_roles: SpaceRoles
  }
}

export type UsersInfo = {
  users_on_spaces: UsersOnSpaces
  saved_sso_users_ids: number[]
  saved_invitations_ids: unknown[]
  saved_sso_users_count: number
  saved_invitations_count: number
  saved_disabled_users_ids: number[]
  saved_external_users_ids: number[]
  saved_internal_users_ids: number[]
  saved_disabled_users_count: number
  saved_external_users_count: number
  saved_internal_users_count: number
}

export type OrgSettings = {
  logo: string
  aad_tenant: string
  merge_users: boolean
  share_plugins: boolean
  s3_asset_bucket: string
  token_timeout_in: string
  sign_saml_request: boolean
  can_add_callback_url: boolean
  editor_url: string
  users_info: UsersInfo
  track_statistics: boolean
  spaces_info?: {
    standard_spaces_count: number
    standard_spaces_ids: number[]
    pro_spaces_count: number
    pro_spaces_ids: number[]
  }
  password_rule_min_length?: number
}

export type OrgBanner = {
  banner_color: 'info' | 'danger' | 'warning'
  banner_text: string
  daily_end_time: string | null
  daily_start_time: string | null
  start_date: string | null
  end_date: string | null
  dismissable: boolean
  scheduled: boolean
  time_limited: boolean
}

export type Org = {
  name: string
  users: User[]
  spaces: OrgSpace[]
  settings: OrgSettings
  custom_settings: OrgSettings
  plan: string
  billing_address: BillingAddress
  euid: string
  user_count: number
  plan_level: number
  max_spaces: number
  max_collaborators: number
  traffic_used: number
  traffic_limit: number
  ai_credits_used: number
  ai_credits_limit: number
  yearly_ai_credits_used: number
  yearly_ai_credits_limit: number
  plans: unknown[]
  external_users: User[]
  extended_external_users: User[]
  track_statistics: boolean
  invitations: InvitedOrgUser[]
  sso_firstname: string
  sso_lastname: string
  sso_alt_email: string
  restricted_regions: RegionServerKeys[]
  strong_auth: boolean
  feature_limits: FeatureLimit[]
  ai_text_generator_disabled: boolean
  confidentiality_disclaimer_enabled?: boolean
  confidentiality_disclaimer_message?: string
  ai_text_generator_feature_disabled: boolean
  force_org_general_ai_output_style?: boolean // TODO - update when API contract defined
  ai_output_styles_strategy?: 'merge' | 'override' | null // TODO - update when API contract defined
  disable_private_spaces: boolean
  token_timeout_in: number
  ai_translation_enabled: boolean
  ideation_room_enabled: boolean
  concept_room_enabled: boolean
  storyblok_lab: {
    allowed_apps: AllowedApp[]
  }
  org_banner?: OrgBanner
  password_rule_min_length?: number
  status?: 'active' | 'disabled' | 'deactivated'
  force_org_ai_styles?: boolean
  allow_space_ai_styles?: boolean
  ai_style_space_composition_mode?: 'override' | 'combine'
}

export type InvitedOrgUser = {
  id: number
  email: string
  org_id: number
  user_id: number
  expires_at: string
  org_role: (typeof OrgRole)[keyof typeof OrgRole] | null
  inviter_id: number
  registered: boolean
  user: User
  external?: boolean
}
